/**
 * Walk/orbit annotate pins — docs/SIM_LAB_ANNOTATE.md
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Html } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { makeAnnotationId } from '../../services/labAnnotations';
import { R1AS_FAUCETS } from '../../data/r1asFaucets';

const HIT_RADIUS = 0.12;
const NOTE_PREVIEW_LEN = 40;
/** Base sphere radii (m) — scaled down further when camera is close */
const PIN_R_HIT = 0.028;
const PIN_R_OTHER = 0.02;
const PIN_SCALE_NEAR = 0.5;
const PIN_SCALE_FAR = 1.2;
const PIN_DIST_NEAR = 0.7;
const PIN_DIST_FAR = 6;
const HOVER_LEAVE_MS = 280;

const _pinWorld = new THREE.Vector3();

function hitRadiusForStation(stationId) {
  const station = R1AS_FAUCETS.find((s) => s.id === stationId);
  if (station?.hitRadius) return station.hitRadius;
  if (station?.type === 'shower') return 0.18;
  return HIT_RADIUS;
}

function previewNote(notes = '') {
  const text = String(notes).replace(/\s+/g, ' ').trim();
  if (!text) return { preview: '', needsMore: false, full: '' };
  if (text.length <= NOTE_PREVIEW_LEN) {
    return { preview: text, needsMore: false, full: text };
  }
  return {
    preview: `${text.slice(0, NOTE_PREVIEW_LEN).trimEnd()}…`,
    needsMore: true,
    full: text,
  };
}

/**
 * Author tag for note peek — docs/SIM_LAB_ANNOTATE.md
 * `jordan.j@buildconstrux.com` → `@jordan.j@buildconstrux`
 */
export function authorHandle(email) {
  const raw = String(email || '').trim();
  if (!raw) return '';
  const stripped = raw.replace(/\.(com|co|io|net|org)$/i, '');
  if (!stripped) return '';
  return stripped.startsWith('@') ? stripped : `@${stripped}`;
}

/**
 * Place pins on click when annotateMode is on.
 */
export function LabAnnotateBridge({
  enabled = false,
  slug,
  kind = 'faucet-hit',
  stationId = 'sink-vanity',
  onAdd,
}) {
  const { camera, gl, scene } = useThree();
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const ndc = useMemo(() => new THREE.Vector2(), []);
  const downRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const el = gl.domElement;

    const onDown = (e) => {
      if (e.button !== 0) return;
      downRef.current = { x: e.clientX, y: e.clientY };
    };

    const onUp = (e) => {
      const down = downRef.current;
      downRef.current = null;
      if (!down || e.button !== 0) return;
      const dx = e.clientX - down.x;
      const dy = e.clientY - down.y;
      if (dx * dx + dy * dy > 64) return;

      const rect = el.getBoundingClientRect();
      ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);

      const roots = [];
      scene.traverse((obj) => {
        if (obj.isMesh && obj.visible && !obj.userData?.annotateIgnore) {
          roots.push(obj);
        }
      });
      const hits = raycaster.intersectObjects(roots, false);
      if (!hits.length) return;

      const hit = hits[0];
      const n = hit.face?.normal
        ? hit.face.normal
            .clone()
            .transformDirection(hit.object.matrixWorld)
            .normalize()
        : new THREE.Vector3(0, 1, 0);

      onAdd?.({
        id: makeAnnotationId(),
        slug,
        kind,
        stationId,
        label: '',
        notes: '',
        position: hit.point.toArray(),
        normal: n.toArray(),
        createdAt: new Date().toISOString(),
      });
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
    };
  }, [enabled, camera, gl, scene, raycaster, ndc, slug, kind, stationId, onAdd]);

  return null;
}

function AnnotationMarker({
  annotation: a,
  selected = false,
  annotateMode = false,
  active = false,
  onHoverEnter,
  onHoverLeave,
  onSelect,
}) {
  const { camera } = useThree();
  const meshRef = useRef(null);
  const matRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const { preview, needsMore, full } = useMemo(
    () => previewNote(a.notes),
    [a.notes]
  );
  const author = useMemo(() => authorHandle(a.userEmail), [a.userEmail]);
  const hasNote = Boolean(full);
  const showPeek = !annotateMode && hasNote && active;

  const isHit = a.kind === 'faucet-hit';
  const isSpout = a.kind === 'faucet-spout';
  const color = selected ? '#c45c3e' : isSpout ? '#4aa8ff' : isHit ? '#1c1c1a' : '#6a6a66';
  const baseR = isHit ? PIN_R_HIT : PIN_R_OTHER;

  useEffect(() => {
    if (!active) setExpanded(false);
  }, [active]);

  useFrame(() => {
    const mesh = meshRef.current;
    const mat = matRef.current;
    if (!mesh || !mat) return;
    mesh.getWorldPosition(_pinWorld);
    const dist = camera.position.distanceTo(_pinWorld);
    const t = THREE.MathUtils.clamp(
      (dist - PIN_DIST_NEAR) / (PIN_DIST_FAR - PIN_DIST_NEAR),
      0,
      1
    );
    // Close → smaller + more solid; far → larger + softer (min scale still visible)
    const scale = THREE.MathUtils.lerp(PIN_SCALE_NEAR, PIN_SCALE_FAR, t);
    mesh.scale.setScalar(scale);
    let opacity = annotateMode
      ? 0.95
      : THREE.MathUtils.lerp(0.96, 0.42, t);
    if (active && hasNote) opacity = Math.max(opacity, 0.9);
    if (selected) opacity = Math.max(opacity, 0.92);
    mat.opacity = opacity;
    mat.color.set(color);
  });

  return (
    <group position={a.position}>
      <mesh
        ref={meshRef}
        userData={{ annotateIgnore: true, annId: a.id }}
        onPointerOver={
          !annotateMode && hasNote
            ? (e) => {
                e.stopPropagation();
                onHoverEnter?.(a.id);
              }
            : undefined
        }
        onPointerOut={
          !annotateMode && hasNote
            ? (e) => {
                e.stopPropagation();
                onHoverLeave?.(a.id);
              }
            : undefined
        }
      >
        <sphereGeometry args={[baseR, 12, 10]} />
        <meshBasicMaterial
          ref={matRef}
          color={color}
          transparent
          opacity={0.5}
          depthWrite={false}
        />
      </mesh>
      {showPeek ? (
        <Html
          center
          zIndexRange={[100, 0]}
          style={{
            pointerEvents: 'auto',
            transform: 'translate3d(-50%, calc(-100% - 10px), 0)',
          }}
          occlude={false}
        >
          <div
            className={`lab-ann-hover${expanded ? ' is-expanded' : ''}`}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerOver={(e) => {
              e.stopPropagation();
              onHoverEnter?.(a.id);
            }}
            onPointerOut={(e) => {
              e.stopPropagation();
              onHoverLeave?.(a.id);
            }}
          >
            {author ? (
              <span className="lab-ann-hover-author">{author}</span>
            ) : null}
            <p className="lab-ann-hover-text">
              {expanded ? full : preview}
            </p>
            {!expanded ? (
              <button
                type="button"
                className="lab-ann-hover-open"
                onClick={(e) => {
                  e.stopPropagation();
                  if (needsMore) setExpanded(true);
                  onSelect?.(a.id);
                }}
              >
                open
              </button>
            ) : null}
          </div>
        </Html>
      ) : null}
    </group>
  );
}

/** Visual markers for all pins */
export function LabAnnotationMarkers({
  annotations = [],
  selectedId = null,
  annotateMode = false,
  onSelect,
}) {
  const [activeId, setActiveId] = useState(null);
  const leaveTimer = useRef(null);

  useEffect(() => {
    if (annotateMode) setActiveId(null);
  }, [annotateMode]);

  useEffect(
    () => () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    },
    []
  );

  const onHoverEnter = (id) => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    setActiveId(id);
  };

  const onHoverLeave = (id) => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => {
      setActiveId((cur) => (cur === id ? null : cur));
      leaveTimer.current = null;
    }, HOVER_LEAVE_MS);
  };

  if (!annotations.length) return null;
  return (
    <group name="lab-annotations">
      {annotations.map((a) => {
        if (!a?.position) return null;
        return (
          <AnnotationMarker
            key={a.id}
            annotation={a}
            selected={a.id === selectedId}
            annotateMode={annotateMode}
            active={activeId === a.id}
            onHoverEnter={onHoverEnter}
            onHoverLeave={onHoverLeave}
            onSelect={onSelect}
          />
        );
      })}
    </group>
  );
}

/**
 * Invisible click proxies from faucet-hit pins (for FaucetPointerBridge).
 */
export function LabAnnotationHitProxies({
  annotations = [],
  onRegister,
}) {
  const hits = annotations.filter((a) => a.kind === 'faucet-hit' && a.position);
  return (
    <group name="lab-ann-hit-proxies">
      {hits.map((a) => (
        <mesh
          key={a.id}
          position={a.position}
          userData={{
            faucetStationId: a.stationId,
            annotateIgnore: true,
          }}
          ref={(obj) => {
            if (obj && a.stationId) onRegister?.(a.id, a.stationId, obj);
            else if (!obj) onRegister?.(a.id, null, null);
          }}
        >
          <sphereGeometry args={[hitRadiusForStation(a.stationId), 12, 10]} />
          <meshBasicMaterial
            transparent
            opacity={0.001}
            depthWrite={false}
            colorWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
}
